import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Typography, TextField } from "@mui/material";
import Searchable from "../../../layout/searchable/Searchable";
import CustomModal from "../../../modal/CustomModal";

//Modals
import EditSingleAmount from "../help/EditSingleAmount";

//Icons
import { ReactComponent as HelpIcon } from "../../../../assets/icons/svg/question-circle.svg";

const EditAmountModal = ({ open, amount, onSave, onCancel }) => {
  // Initial values from amount (edit) or blank (add)
  const [ingredient, setIngredient] = useState(amount?.ingredient || null);
  const [unit, setUnit] = useState(amount?.unit?.display || null);
  const [value, setValue] = useState(amount?.unit?.display?.magnitude || amount?.amount || "");
  const [state, setState] = useState(amount?.state || "");
  const [note, setNote] = useState(amount?.note || "");
  const [showOptionalFields, setShowOptionalFields] = useState(!!(amount?.state || amount?.note));

  //Modal state
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Validation
  const [ingredientError, setIngredientError] = useState("");
  const [unitError, setUnitError] = useState("");
  const [amountError, setAmountError] = useState("");

  console.log(amount);

  useEffect(() => {
    setIngredient(amount?.ingredient || null);
    setUnit(amount?.unit?.display || null);
    setValue(amount?.unit?.display?.magnitude || amount?.amount || "");
    setState(amount?.state || "");
    setNote(amount?.note || "");
    setShowOptionalFields(!!(amount?.state || amount?.note));
    setIngredientError("");
    setUnitError("");
    setAmountError("");
  }, [open, amount]);

  const verifyAmount = (val) => {
    let error = "";
    if (!val || val === "") error = "Amount is required";
    else if (isNaN(val) || Number(val) <= 0) error = "Amount must be a positive number";
    return error;
  };
  const verifyIngredient = (item) => {
    let error = "";
    if (!item || !item.name) error = "Ingredient is required";
    return error;
  };
  const verifyUnit = (item) => {
    let error = "";
    if (!item || !item.text) error = "Unit is required";
    return error;
  };

  const handleSave = () => {
    const ingErr = verifyIngredient(ingredient);
    const unitErr = verifyUnit(unit);
    const amtErr = verifyAmount(value);
    setIngredientError(ingErr);
    setUnitError(unitErr);
    setAmountError(amtErr);
    if (ingErr || unitErr || amtErr) return;
    // Compose normalized amount object
    const newAmount = {
      ingredient: ingredient,
      unit: {
        display: unit,
        anchor: unit, // Placeholder: backend normalization will handle anchor
      },
      amount: Number(value),
      state: state,
      note: note,
      date: amount?.date || new Date(),
    };
    onSave(newAmount);
  };

  return (
    <CustomModal open={open} handleClose={onCancel} type="bottom">
      <div className="w-full">
        <CustomModal open={showHelpModal} handleClose={() => setShowHelpModal(false)} type="bottom">
          <EditSingleAmount />
        </CustomModal>
        <div className="p-4 bg-neutral-100 w-full flex flex-row justify-between items-center">
          <Typography variant="h6" className="font-normal">
            {amount ? "Edit Amount" : "Add Amount"}
          </Typography>
          <Button
            size="small"
            disableRipple
            className="hover:bg-transparent border-neutral-400 hover:border-neutral-500"
            variant="outlined"
            onClick={() => setShowHelpModal(true)}
          >
            <HelpIcon className="w-4 h-4 mr-1" />
            Help
          </Button>
        </div>
        <div className="w-full p-4">
          <div className="flex flex-row gap-2 w-full mb-4">
            <div className="w-full">
              <TextField
                label="Amount"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setAmountError(verifyAmount(e.target.value));
                }}
                type="number"
                inputProps={{ min: 0, step: "any" }}
                className="w-full"
                error={!!amountError}
                helperText={amountError}
              />
            </div>
            <div className="w-full">
              <Searchable
                label="Unit"
                multiple={false}
                apiUrl="/units/searchable"
                onItemChange={(item) => {
                  setUnit(item);
                  setUnitError(verifyUnit(item));
                }}
                selectedItemsFromParent={unit ? [unit] : []}
                error={!!unitError}
                helperText={unitError}
                className="w-full"
              />
            </div>
          </div>
          <Searchable
            label="Ingredient"
            multiple={false}
            apiUrl="/ingredients/searchable"
            onItemChange={(item) => {
              setIngredient(item);
              setIngredientError(verifyIngredient(item));
            }}
            selectedItemsFromParent={ingredient ? [ingredient] : []}
            error={!!ingredientError}
            helperText={ingredientError}
          />
          <div className="mt-4">
            {showOptionalFields ? (
              <>
                <Button
                  onClick={() => setShowOptionalFields(false)}
                  disableRipple
                  className="hover:bg-transparent justify-start"
                >
                  <Typography variant="subtitle2" className="text-secondaryText-600 font-normal text-xs">
                    - Hide Notes/Ingredient State
                  </Typography>
                </Button>
                <div className="mt-2 flex flex-col w-full gap-4">
                  <TextField
                    label="State"
                    variant="outlined"
                    fullWidth
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    inputProps={{ maxLength: 32 }}
                  />
                  <TextField
                    label="Notes"
                    variant="outlined"
                    fullWidth
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    inputProps={{ maxLength: 128 }}
                  />
                </div>
              </>
            ) : (
              <Button
                onClick={() => setShowOptionalFields(true)}
                disableRipple
                className="hover:bg-transparent justify-start"
              >
                <Typography variant="subtitle2" className="text-secondaryText-600 font-normal text-xs">
                  + Add Notes/Ingredient State
                </Typography>
              </Button>
            )}
          </div>
          <div className="flex justify-between mt-6 gap-2">
            <Button
              variant="contained"
              color="secondary"
              className="text-primaryText"
              onClick={onCancel}
              disableRipple
              disableElevation
            >
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disableRipple disableElevation>
              Save
            </Button>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

EditAmountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  amount: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default EditAmountModal;
